import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Box, Container, SvgIcon, TextField, Typography } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { Label } from "../common/Label/Label";
import { CustomTextField } from "../common/CustomTextField/CustomTextField";
import { Button } from "../common/Button/Button";
import {
  PageSetupHolder,
  PageSetup,
  LogArea,
  LeftSide,
  TrialSetupPage,
  RightSide,
} from "./style";
import axios from "axios";
import { BrightHiveLogo } from "./BrightHiveLogo";
import { formatLabel } from "../helpers";
import { useState } from "react";
import { toast } from "react-toastify";
import { NavbarWrapper } from "../common/Navbar/style";
import { ReactComponent as brighthiveImage1 } from "../assets/brighthive-theme/brighthiveImage1.svg";
import { Lock, Visibility, VisibilityOff } from "@mui/icons-material";
import PasswordRequirements from "./PasswordRequirements";

const AUTH_TOKEN = process.env.REACT_APP_SETUP_AUTH_TOKEN;
const SETUP_URL = process.env.REACT_APP_SETUP_URL;

export const SetPassword = () => {
  const [searchParams] = useSearchParams();
  const [isSubmitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [show, setShow] = useState({
    password: false,
    confirmPassword: false,
  });

  const toggleVisibility = (field: keyof typeof show) => {
    setShow((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };
  // Password validation rules
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const email = searchParams.get("email");
  const code = searchParams.get("code");

  return (
    <PageSetupHolder className="dark">
      <NavbarWrapper
        className="NavbarWrapper"
        role="banner"
        sx={{
          backgroundColor: "transparent",
          padding: "20px 10px",
        }}
      >
        <BrightHiveLogo />
        <SvgIcon
          className="brighthive-image"
          component={brighthiveImage1}
          viewBox="0 0 1444 314"
        />
      </NavbarWrapper>
      <TrialSetupPage>
        <LeftSide>
          <Formik
            validationSchema={Yup.object().shape({
              password: Yup.string().required("Password is required"),
              confirmPassword: Yup.string()
                .oneOf([Yup.ref("password"), null], "Passwords must match")
                .required("Confirm Password is required"),
            })}
            initialValues={{
              password: "",
            }}
            onSubmit={async (values) => {
              const { password } = values;

              // Password validation rules
              const hasUppercase = /[A-Z]/.test(password);
              const hasLowercase = /[a-z]/.test(password);
              const hasNumber = /\d/.test(password);
              const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
              const hasMinLength = password.length >= 8;

              const isPasswordValid =
                hasUppercase &&
                hasLowercase &&
                hasNumber &&
                hasSpecialChar &&
                hasMinLength;

              if (!isPasswordValid) {
                toast({
                  title: "Invalid Password",
                  description:
                    "Please ensure your password meets all requirements.",
                  variant: "destructive",
                });
                return;
              }
              setSubmitting(true);
              axios
                .post(
                  `${SETUP_URL}/adminapi/userapi/confirmuser?email=${email}&code=${code}`,
                  {
                    password: values.password,
                  },
                  {
                    headers: {
                      Authorization: `Bearer ${AUTH_TOKEN}`,
                    },
                  }
                )
                .then((res) => navigate("/welcome"))
                .catch((err) => {
                  // debugger;
                  const msg =
                    err?.response.data?.error?.message?.value || err?.message;
                  toast.error(msg);
                })
                .finally(() => setSubmitting(false));
            }}
          >
            {({
              values,
              handleChange,
              handleBlur,
              handleSubmit,
              errors,
              touched,
              isValid,
            }) => {
              const hasUppercase = /[A-Z]/.test(values.password);
              const hasLowercase = /[a-z]/.test(values.password);
              const hasNumber = /\d/.test(values.password);
              const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(
                values.password
              );
              const hasMinLength = values.password.length >= 8;

              const isPasswordValid =
                hasUppercase &&
                hasLowercase &&
                hasNumber &&
                hasSpecialChar &&
                hasMinLength;

              return (
                <Box className="trial-setup-form set-password">
                  <Box
                    textAlign={"center"}
                    color={"gray.dark"}
                    sx={{
                      width: "64px",
                      height: "64px",
                      backgroundColor: "rgb(201 242 234 / 0.5)",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 20px",
                      color: "primary.dark",
                    }}
                  >
                    <Lock width={32} height={32} />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "20px 0",
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      textAlign={"center"}
                      color={"common.black"}
                    >
                      Set Your Password
                    </Typography>
                    <Typography
                      variant="body1"
                      textAlign={"center"}
                      color={"gray.dark"}
                    >
                      Create a secure password for your account
                    </Typography>
                  </Box>
                  <form onSubmit={handleSubmit}>
                    <Box mt={4}>
                      {["password", "confirmPassword"].map((item) => (
                        <Box key={item} mt={5}>
                          <Label
                            mb={1}
                            width={"15.5rem"}
                            value={
                              <>
                                {formatLabel(item)}{" "}
                                <span style={{ color: "rgb(199, 20, 41)" }}>
                                  *
                                </span>
                              </>
                            }
                          />
                          <Box
                            sx={{
                              position: "relative",
                            }}
                          >
                            <CustomTextField
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={(values as any)?.[`${item}`]}
                              error={(errors as any)?.[`${item}`]}
                              touched={(touched as any)?.[`${item}`]}
                              type={
                                show[item as keyof typeof show]
                                  ? "text"
                                  : "password"
                              }
                              name={item}
                              fullWidth
                              inputProps={{
                                sx: {
                                  fontSize: "1rem",
                                  padding: "11px 40px 11px 11px !important",
                                },
                              }}
                              size="small"
                            />
                            <Button
                              sx={{
                                position: "absolute",
                                right: "0",
                                top: "0",
                              }}
                              onClick={() =>
                                toggleVisibility(item as keyof typeof show)
                              }
                            >
                              {show[item as keyof typeof show] ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </Button>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                        margin: "20px 0",
                        backgroundColor: "custom.light",
                        padding: "10px",
                        borderRadius: "0.375rem",
                      }}
                    >
                      <Typography variant="body3" color={"gray.dark"}>
                        <PasswordRequirements password={values.password} />
                      </Typography>
                    </Box>
                    <Box
                      className="trial-setup-form-footer"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "20px 0",
                        gap: "10px",
                      }}
                    >
                      <Button
                        variant="contained"
                        type="submit"
                        className="btn"
                        disabled={isSubmitting || !isPasswordValid || !isValid}
                        sx={{
                          width: "100%",
                          height: "50px !important",
                          padding: "15px !important",
                          fontSize: "1rem !important",
                          fontWeight: "600 !important",
                          borderRadius: "0.375rem !important",
                        }}
                      >
                        Confirm Password
                      </Button>
                    </Box>
                  </form>
                </Box>
              );
            }}
          </Formik>
        </LeftSide>
        <RightSide>
          <Box>
            <Typography
              variant="h5"
              fontWeight={600}
              textAlign={"center"}
              color={"common.black"}
            >
              Transform Your Data Into Insights
            </Typography>
            <Typography
              variant="body1"
              textAlign={"center"}
              color={"gray.dark"}
            >
              Join leading companies using Brighthive to unlock the power of
              their data
            </Typography>
          </Box>
          <img src="/homePageImage.png" alt="brighthive" />
        </RightSide>
      </TrialSetupPage>
    </PageSetupHolder>
  );
};
